import { useState } from "react";
import type { RiskLevel } from "@/lib/risk";
import { track } from "@/lib/analytics";
import { ShareRow } from "./share-row";

interface ShareCardProps {
  title: string;
  subtitle?: string;
  level?: RiskLevel;
  badge?: string;
  kind?: "state" | "tickcheck" | "guide" | "home";
  /** A state slug, so "Save the image" saves that state's own card. */
  state?: string;
  /** With `state`, a county slug, so the image is that county's own card. */
  county?: string;
  /** plain-text summary for the "copy summary" button */
  summaryText: string;
  sharePath: string;
  shareText: string;
}

function ogPath(p: ShareCardProps): string {
  const sp = new URLSearchParams();
  sp.set("title", p.title);
  if (p.state) sp.set("state", p.state);
  if (p.state && p.county) sp.set("county", p.county);
  if (p.subtitle) sp.set("subtitle", p.subtitle);
  if (p.badge) sp.set("badge", p.badge);
  if (p.level) sp.set("level", p.level);
  sp.set("kind", p.kind ?? "guide");
  return `/api/og?${sp.toString()}`;
}

/**
 * "Pass it on", as a short note at the end of a page. It used to be a boxed mock-up of
 * the share image (with a bug icon standing in for a logo), a pill row and seven
 * buttons. The image still exists — "Save the image" downloads it — it just isn't
 * previewed in the page any more.
 */
export function ShareCard(props: ShareCardProps) {
  const [copied, setCopied] = useState(false);

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(props.summaryText);
      setCopied(true);
      track("share_clicked", { method: "copy_summary", path: props.sharePath });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const item =
    "font-medium text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-current";

  return (
    <aside aria-label="Share this page" className="no-print border-t border-border pt-5">
      <p className="font-display text-lg text-foreground">
        Know someone who hikes, hunts, gardens or walks a dog?
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Most people have never heard of alpha-gal syndrome. Sending them this page is the most
        useful thing you can do with it.
      </p>
      <ShareRow
        className="mt-3"
        text={props.shareText}
        path={props.sharePath}
        extra={[
          <button
            key="summary"
            type="button"
            onClick={copySummary}
            className={item}
            aria-live="polite"
          >
            {copied ? "Summary copied" : "Copy a summary"}
          </button>,
          <a
            key="image"
            href={ogPath(props)}
            target="_blank"
            rel="noopener noreferrer"
            download
            onClick={() => track("share_clicked", { method: "save_image", path: props.sharePath })}
            className={item}
          >
            Save the image
          </a>,
        ]}
      />
    </aside>
  );
}
